// ============================================================================
// CLOSE THE FRONT OFFICE DAY (PMS Phase 2 slice 7d)
// ============================================================================
// The cashier counts the drawer (by denomination when the property counts that
// way) against what it should hold — opening float + cash received − cash
// refunded and paid out — and gives the reason for any difference. Every rupee
// counted is then accounted for: handed to the Main Cashier, deposited in the
// bank, or kept as tomorrow's float. Each non-cash method is confirmed against
// its own channel (terminal settlement, UPI / bank statement). The server checks
// the same rules (BR-FOC-003…005); a closed day locks its receipts.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { SyutSelect, type SelectOption } from 'components/ui';
import { bankAccountService } from 'api/services/bankAccountService';
import { foClosingService } from 'api/services/foClosingService';
import { formatCurrency } from 'utils/currencyFormat';
import type { FoCloseRequest, FoDay } from 'api/types/foClosing.types';

interface Props {
  day: FoDay;
  onClose: () => void;
  onClosed: (day: FoDay) => void;
}

const num = (v: string) => { const n = parseFloat(v); return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0; };
const has = (v: string) => v.trim() !== '' && Number.isFinite(parseFloat(v));

const FoCloseModal: React.FC<Props> = ({ day, onClose, onClosed }) => {
  const nonCash = useMemo(() => day.modes.filter((m) => !m.isCash), [day.modes]);
  const [opening, setOpening] = useState(day.openingFromPreviousClose ? '' : String(day.openingFloat || ''));
  const [counted, setCounted] = useState('');
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [differenceReason, setDifferenceReason] = useState('');
  const [handed, setHanded] = useState('');
  const [handedTo, setHandedTo] = useState('');
  const [handedRef, setHandedRef] = useState('');
  const [deposited, setDeposited] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [slip, setSlip] = useState('');
  const [retained, setRetained] = useState('');
  const [remarks, setRemarks] = useState('');
  const [confirm, setConfirm] = useState<Record<string, { amount: string; ref: string; reason: string }>>(() =>
    Object.fromEntries(nonCash.map((m) => [m.paymentMode, { amount: m.systemAmount.toFixed(2), ref: '', reason: '' }])));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-opened day: start from what was entered at the last close.
  useEffect(() => {
    if (day.status !== 'REOPENED') return;
    if (day.countedCash != null) setCounted(String(day.countedCash));
    if (day.denominations) setCounts(Object.fromEntries(Object.entries(day.denominations).map(([k, v]) => [k, String(v)])));
    setDifferenceReason(day.differenceReason ?? '');
    setHanded(day.handedOverAmount ? String(day.handedOverAmount) : '');
    setHandedTo(day.handedOverToName ?? '');
    setHandedRef(day.handedOverReference ?? '');
    setDeposited(day.depositedAmount ? String(day.depositedAmount) : '');
    setBankAccountId(day.depositBankAccountId ?? '');
    setSlip(day.depositSlipNumber ?? '');
    setRetained(day.retainedFloat ? String(day.retainedFloat) : '');
    setRemarks(day.remarks ?? '');
    setConfirm((c) => {
      const next = { ...c };
      day.modes.forEach((m) => {
        if (!m.isCash && m.confirmedAmount != null)
          next[m.paymentMode] = { amount: String(m.confirmedAmount), ref: m.confirmationRef ?? '', reason: m.differenceReason ?? '' };
      });
      return next;
    });
  }, [day]);

  const openingFloat = day.openingFromPreviousClose ? day.openingFloat : num(opening);
  const expected = Math.round((day.expectedCash - day.openingFloat + openingFloat) * 100) / 100;
  const countedCash = day.countByDenomination
    ? Math.round(Object.entries(counts).reduce((s, [d, c]) => s + Number(d) * Math.max(0, num(c)), 0) * 100) / 100
    : num(counted);
  const difference = Math.round((countedCash - expected) * 100) / 100;
  const disposed = Math.round((num(handed) + num(deposited) + num(retained)) * 100) / 100;
  const unaccounted = Math.round((countedCash - disposed) * 100) / 100;

  const problems: string[] = [];
  if (!day.countByDenomination && !has(counted)) problems.push('Enter the cash counted.');
  if (difference !== 0 && differenceReason.trim().length < 3) problems.push(`Cash is ${difference < 0 ? 'short' : 'over'} — give the reason.`);
  if (unaccounted !== 0) problems.push(`Handed over + deposited + retained must equal the cash counted (${formatCurrency(unaccounted)} not accounted for).`);
  if (num(handed) > 0 && !handedTo.trim()) problems.push('Say who received the cash handed over.');
  if (num(deposited) > 0 && (!bankAccountId || !slip.trim())) problems.push('A deposit needs the bank account and the slip number.');
  nonCash.forEach((m) => {
    const c = confirm[m.paymentMode];
    if (!c || !has(c.amount)) problems.push(`Confirm the ${m.paymentMode} amount.`);
    else if (num(c.amount) !== m.systemAmount && c.reason.trim().length < 3) problems.push(`${m.paymentMode} differs — give the reason.`);
  });

  const loadBankAccounts = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await bankAccountService.getForBooking(day.propertyId);
    return (res.data || [])
      .filter((o) => !input || `${o.accountCode} ${o.accountName} ${o.bankName}`.toLowerCase().includes(input.toLowerCase()))
      .map((o) => ({ value: o.id, label: `${o.accountCode} – ${o.accountName} (${o.bankName})` }));
  }, [day.propertyId]);

  const save = useCallback(async () => {
    const request: FoCloseRequest = {
      openingFloat: day.openingFromPreviousClose ? undefined : openingFloat,
      countedCash: day.countByDenomination ? undefined : countedCash,
      denominations: day.countByDenomination
        ? Object.fromEntries(Object.entries(counts).filter(([, c]) => num(c) > 0).map(([d, c]) => [d, num(c)]))
        : undefined,
      differenceReason: differenceReason.trim() || undefined,
      handedOverAmount: num(handed),
      handedOverToName: handedTo.trim() || undefined,
      handedOverReference: handedRef.trim() || undefined,
      depositedAmount: num(deposited),
      depositBankAccountId: num(deposited) > 0 ? bankAccountId || undefined : undefined,
      depositSlipNumber: num(deposited) > 0 ? slip.trim() || undefined : undefined,
      retainedFloat: num(retained),
      modes: nonCash.map((m) => ({
        paymentMode: m.paymentMode,
        confirmedAmount: num(confirm[m.paymentMode]?.amount ?? ''),
        confirmationRef: confirm[m.paymentMode]?.ref.trim() || undefined,
        differenceReason: confirm[m.paymentMode]?.reason.trim() || undefined,
      })),
      remarks: remarks.trim() || undefined,
      rowVersion: day.rowVersion,
    };
    setSaving(true);
    setError(null);
    try {
      const closed = await foClosingService.close(day.propertyId, day.businessDate.slice(0, 10), request);
      notificationService.toast.success('Front Office day closed.');
      onClosed(closed);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'The day was not closed.');
    } finally {
      setSaving(false);
    }
  }, [day, openingFloat, countedCash, counts, differenceReason, handed, handedTo, handedRef, deposited, bankAccountId, slip, retained,
    nonCash, confirm, remarks, onClosed]);

  const row = (label: React.ReactNode, value: React.ReactNode, strong = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontWeight: strong ? 600 : undefined }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
  const sectionTitle = (t: string) => <div style={{ fontWeight: 600, fontSize: '0.85rem', margin: '0.75rem 0 0.35rem' }}>{t}</div>;
  const small = { height: '1.9rem', fontSize: '0.82rem' } as const;

  return (
    <>
      <div className="modal-backdrop" style={{ zIndex: 10050 }} />
      <div className="modal-container modal-lg" style={{ zIndex: 10051 }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Close the Front Office day — {new Date(day.businessDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content" style={{ maxHeight: '72vh', overflowY: 'auto', fontSize: '0.83rem' }}>
          {sectionTitle('Cash in the drawer')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ padding: '0.5rem 0.7rem', borderRadius: 6, background: 'var(--bg-secondary)' }}>
              {row('Opening float', day.openingFromPreviousClose
                ? <span title="Retained at the previous close">{formatCurrency(day.openingFloat)}</span>
                : <input type="number" className="form-input" style={{ ...small, width: 120, textAlign: 'right' }} min={0} step={0.01}
                    value={opening} onChange={(e) => setOpening(e.target.value)} placeholder="First close" />)}
              {row('Add: cash received', formatCurrency(day.cashReceived))}
              {row('Less: refunded and paid out', formatCurrency(day.cashPaidOut))}
              {row('Expected', formatCurrency(expected), true)}
            </div>
            <div>
              {day.countByDenomination ? (
                <table className="data-table" style={{ width: '100%', fontSize: '0.8rem' }}>
                  <thead><tr><th style={{ textAlign: 'left' }}>Note / coin</th><th style={{ textAlign: 'right' }}>Count</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
                  <tbody>
                    {day.denominationValues.map((d) => (
                      <tr key={d}>
                        <td>{d}</td>
                        <td style={{ textAlign: 'right' }}>
                          <input type="number" className="form-input" style={{ ...small, width: 80, textAlign: 'right' }} min={0} step={1}
                            value={counts[String(d)] ?? ''} onChange={(e) => setCounts((c) => ({ ...c, [String(d)]: e.target.value }))} />
                        </td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(d * Math.max(0, num(counts[String(d)] ?? '')))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="form-group">
                  <label className="form-label">Cash counted <span className="text-danger">*</span></label>
                  <input type="number" className="form-input" style={{ maxWidth: 180, textAlign: 'right' }} min={0} step={0.01}
                    value={counted} onChange={(e) => setCounted(e.target.value)} autoFocus />
                </div>
              )}
              {row('Counted', formatCurrency(countedCash), true)}
              {row(difference < 0 ? 'Short' : difference > 0 ? 'Over' : 'Difference',
                <span style={{ color: difference === 0 ? undefined : 'var(--danger, #dc2626)' }}>{formatCurrency(difference)}</span>, true)}
            </div>
          </div>
          {difference !== 0 && (
            <div className="form-group" style={{ marginTop: '0.5rem' }}>
              <label className="form-label">Reason for the difference <span className="text-danger">*</span></label>
              <input type="text" className="form-input" maxLength={500} value={differenceReason} onChange={(e) => setDifferenceReason(e.target.value)} />
            </div>
          )}

          {sectionTitle('Where the cash went')}
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: '0.4rem 0.75rem', alignItems: 'center' }}>
            <span>Handed to the Main Cashier</span>
            <input type="number" className="form-input" style={{ ...small, textAlign: 'right' }} min={0} step={0.01} value={handed} onChange={(e) => setHanded(e.target.value)} />
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <input type="text" className="form-input" style={small} maxLength={150} placeholder="Received by" value={handedTo} onChange={(e) => setHandedTo(e.target.value)} />
              <input type="text" className="form-input" style={{ ...small, maxWidth: 130 }} maxLength={60} placeholder="Receipt ref" value={handedRef} onChange={(e) => setHandedRef(e.target.value)} />
            </div>
            <span>Deposited in the bank</span>
            <input type="number" className="form-input" style={{ ...small, textAlign: 'right' }} min={0} step={0.01} value={deposited} onChange={(e) => setDeposited(e.target.value)} />
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SyutSelect value={bankAccountId} onChange={(v) => setBankAccountId((v as string) || '')} isAsync loadOptions={loadBankAccounts}
                  defaultOptions cacheOptions={false} placeholder="Bank account" menuPortalTarget={document.body} size="sm" isDisabled={num(deposited) <= 0} />
              </div>
              <input type="text" className="form-input" style={{ ...small, maxWidth: 130 }} maxLength={60} placeholder="Slip no." value={slip}
                disabled={num(deposited) <= 0} onChange={(e) => setSlip(e.target.value)} />
            </div>
            <span>Retained as float</span>
            <input type="number" className="form-input" style={{ ...small, textAlign: 'right' }} min={0} step={0.01} value={retained} onChange={(e) => setRetained(e.target.value)} />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Tomorrow's opening float</span>
          </div>
          <div style={{ marginTop: '0.35rem', color: unaccounted === 0 ? 'var(--text-muted)' : 'var(--danger, #dc2626)' }}>
            Accounted for {formatCurrency(disposed)} of {formatCurrency(countedCash)}
            {unaccounted !== 0 && <> · <b>{formatCurrency(unaccounted)}</b> {unaccounted > 0 ? 'not accounted for' : 'more than counted'}</>}
          </div>

          {nonCash.length > 0 && (
            <>
              {sectionTitle('Card, UPI and other methods — confirm against the channel')}
              <table className="data-table" style={{ width: '100%', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Method</th><th style={{ textAlign: 'right' }}>System</th>
                    <th style={{ textAlign: 'right' }}>Confirmed</th><th style={{ textAlign: 'left' }}>Batch / statement ref</th>
                    <th style={{ textAlign: 'left' }}>Reason for a difference</th>
                  </tr>
                </thead>
                <tbody>
                  {nonCash.map((m) => {
                    const c = confirm[m.paymentMode] ?? { amount: '', ref: '', reason: '' };
                    const set = (patch: Partial<typeof c>) => setConfirm((all) => ({ ...all, [m.paymentMode]: { ...c, ...patch } }));
                    const diff = has(c.amount) ? Math.round((num(c.amount) - m.systemAmount) * 100) / 100 : 0;
                    return (
                      <tr key={m.paymentMode}>
                        <td>{m.paymentMode} <span style={{ color: 'var(--text-muted)' }}>· {m.receiptCount}</span></td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(m.systemAmount)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <input type="number" className="form-input" style={{ ...small, width: 110, textAlign: 'right' }} step={0.01}
                            value={c.amount} onChange={(e) => set({ amount: e.target.value })} />
                          {diff !== 0 && <div style={{ color: 'var(--danger, #dc2626)', fontSize: '0.72rem' }}>{formatCurrency(diff)}</div>}
                        </td>
                        <td><input type="text" className="form-input" style={small} maxLength={60} value={c.ref} onChange={(e) => set({ ref: e.target.value })} /></td>
                        <td><input type="text" className="form-input" style={small} maxLength={500} value={c.reason} disabled={diff === 0} onChange={(e) => set({ reason: e.target.value })} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}

          <div className="form-group" style={{ marginTop: '0.75rem' }}>
            <label className="form-label">Remarks</label>
            <input type="text" className="form-input" maxLength={500} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
          {day.unrecordedCount > 0 && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {day.unrecordedCount} receipt(s) of the day were recorded before collection points existed and are not in this close.
            </div>
          )}
          {error && <div className="form-error-message">{error}</div>}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <span style={{ marginRight: 'auto', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {problems[0] ?? "Closing locks the day's Front Office receipts."}
          </span>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => { void save(); }} disabled={saving || problems.length > 0}>
            {saving ? 'Closing…' : 'Close the day'}
          </button>
        </div>
      </div>
    </>
  );
};

export default FoCloseModal;
